import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { adminSubtypesApi } from '@/api/curatorSubtypes';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { SearchInput } from '@/components/ui/SearchInput';
import { useAuthStore } from '@/stores/auth';
import { curatorGroupsErrorText } from '@/lib/curatorGroupErrors';
import { staleSubtypes, subtypesChanged, toggleSubtype } from '@/lib/curatorSubtypes';
import { visibleSubtypes } from '@/lib/curatorCardView';

/**
 * Подтипы марафонов у куратора — то, что открывает ему курсы и учеников (D40,
 * договор `docs/CONTRACTS/curator-groups.md` §1).
 *
 * ⚠️ ПОКА ЗДЕСЬ НИЧЕГО НЕ ОТМЕЧЕНО, КУРАТОР НЕ ВИДИТ НИЧЕГО: ни курса, ни
 * ученика, ни своей группы. На 12.09.2026 подтипы не проставлены ни одному
 * куратору из 90 — то есть этот блок и есть вход в весь D40.
 *
 * ⚠️ СОХРАНЕНИЕ — ПОЛНАЯ ЗАМЕНА НАБОРА. Сервер перезаписывает список целиком,
 * поэтому экран всегда шлёт всё, что отмечено, а не разницу.
 *
 * ⚠️ СМОТРЕТЬ МОГУТ ADMIN И MANAGER, СТАВИТЬ — ТОЛЬКО ADMIN (решение владельца
 * 12.09.2026). Менеджеру кнопку не показываем: нажатие всё равно вернуло бы 403,
 * а причину человек не узнал бы.
 */
export function CuratorSubtypesCard({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.user?.role) === 'admin';

  const view = useQuery({
    queryKey: ['admin', 'user', userId, 'subtypes'],
    queryFn: () => adminSubtypesApi.get(userId),
  });

  const saved = useMemo(() => view.data?.subtypes ?? [], [view.data]);
  const available = view.data?.available;
  const [draft, setDraft] = useState<string[]>([]);
  const [q, setQ] = useState('');

  // Черновик идёт за сервером: после сохранения и перечитывания он равен ответу.
  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const save = useMutation({
    mutationFn: (subtypes: string[]) => adminSubtypesApi.put(userId, subtypes),
    onSuccess: (res) => {
      qc.setQueryData(['admin', 'user', userId, 'subtypes'], res);
      // В карточке пользователя те же подтипы приходят полем `curator_subtypes`.
      qc.invalidateQueries({ queryKey: ['admin', 'student', userId] });
      qc.invalidateQueries({ queryKey: ['admin', 'curators'] });
    },
  });

  const stale = staleSubtypes(saved, available);
  const shown = visibleSubtypes(available ?? [], saved, q);
  const changed = subtypesChanged(saved, draft);

  return (
    <Card className="mb-4">
      <CardTitle>{t('adminSubtypes.title')}</CardTitle>
      <p className="mt-1 text-sm text-on-surface-variant">{t('adminSubtypes.hint')}</p>

      {view.isLoading ? (
        <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
      ) : view.isError ? (
        <p className="mt-3 text-sm text-error">{curatorGroupsErrorText(view.error, t)}</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {saved.length ? (
              saved.map((s) => <Pill key={s} variant="primary">{s}</Pill>)
            ) : (
              <Pill variant="neutral">{t('adminSubtypes.none')}</Pill>
            )}
          </div>

          {stale.map((s) => (
            <p key={s} className="mt-2 text-sm text-error">{t('adminSubtypes.staleSubtype', { subtype: s })}</p>
          ))}

          {!available?.length ? (
            <p className="mt-3 text-sm text-on-surface-variant">{t('adminSubtypes.empty')}</p>
          ) : (
            <>
            {/* Поиск, отмеченные сверху и прокрутка на ~6 строк (28.09.2026, вторая волна п. 8). Фильтр — только
                для показа: сохраняется весь черновик. */}
            <SearchInput
              className="mt-3"
              value={q}
              placeholder={t('adminSubtypes.searchPlaceholder')}
              onSearch={setQ}
            />
            <p className="mt-2 text-xs text-on-surface-variant">
              {t('common.shownOf', { shown: shown.length, total: available.length })}
            </p>
            <ul className="mt-2 max-h-96 space-y-2 overflow-y-auto pr-1">
              {shown.map((a) => (
                <li key={a.subtype} data-subtype={a.subtype}>
                  <label className="flex items-start gap-3 rounded-card bg-surface-container-lowest px-3 py-2">
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 flex-none accent-primary"
                      checked={draft.includes(a.subtype)}
                      disabled={!canEdit}
                      onChange={() => setDraft((prev) => toggleSubtype(prev, a.subtype))}
                    />
                    <span className="min-w-0">
                      <span className="block font-semibold text-on-surface">{a.subtype}</span>
                      <span className="block text-xs text-on-surface-variant">
                        {a.marathons.map((m) => m.title).join(' · ')}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            </>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {canEdit ? (
              <Button disabled={!changed} loading={save.isPending} onClick={() => save.mutate(draft)}>
                {t('adminSubtypes.save')}
              </Button>
            ) : (
              <p className="text-sm text-on-surface-variant">{t('adminSubtypes.readOnlyHint')}</p>
            )}
            <Button variant="ghost" size="sm" onClick={() => view.refetch()}>
              {t('adminSubtypes.refresh')}
            </Button>
          </div>

          {save.isError && <p className="mt-2 text-sm text-error">{curatorGroupsErrorText(save.error, t)}</p>}
          {save.isSuccess && !save.isError && !changed && (
            <p className="mt-2 text-sm text-success">{t('adminSubtypes.saved')}</p>
          )}
        </>
      )}
    </Card>
  );
}
